// web-v2/src/lib/marketChannel.js
//
// HOW A SUBMISSION REACHED THE CARRIER, derived ONCE. Prompt 93.
//
// Two doors now log a submission by hand — the phantom row on the markets
// surface and the long-form LogSubmissionDialog — and both need the same three
// answers, the same prefill from the carrier, and the same "cobroker means you
// must name the wholesaler" rule. Two copies of that would agree on the common
// case and disagree on the day it matters, which is the shape One Fact One
// Derivation exists to stop. This module is the one copy.
//
// THE VOCABULARY MIRRORS THE SERVER'S. services/submission-markets.service.js
// MARKET_CHANNELS is ['api', 'portal', 'email', 'cobroker'] and recordSend 400s
// on anything else. tests/phantom-market-row.test.js pins the two together.
//
// 'api' IS DELIBERATELY NOT OFFERED HERE. It is a channel TRACON fires itself
// (recordApiSubmission writes it, and it writes the frozen payload alongside),
// so a human logging a submission by hand did not use it. Offering it would let
// a hand-typed row claim a machine observation that never happened.

export const MARKET_CHANNEL_OPTIONS = [
  { value: 'email', label: 'Direct email' },
  { value: 'portal', label: 'Carrier portal' },
  { value: 'cobroker', label: 'Through a wholesaler' },
];

// The unselected sentinel. It is NOT an answer, it cannot be saved, and the
// server 400s on it independently. "I would rather not say" used to sit here as
// the DEFAULT, so every submission logged without a deliberate change recorded
// an unknown transport. David: "This isn't a survey, it's a business process."
export const CHANNEL_UNSELECTED = '';

// The question, asked the same way in both doors. It is a QUESTION and not a
// placeholder: "Pick how it went" replaced the question with an instruction.
export const CHANNEL_PROMPT = 'How did you send it?';

// routing_type on the carriers table IS the per-carrier default channel, and
// prompt 67 asked whether one existed: it does, on all 23 active carriers
// (via_cobroker 8, direct_email 13, api 2, portal 2). NO DEFAULTS ARE INVENTED
// here — a carrier whose routing_type does not map returns the unselected
// sentinel and the broker states it.
const ROUTING_TO_CHANNEL = {
  via_cobroker: 'cobroker',
  direct_email: 'email',
  portal: 'portal',
  // api: deliberately absent. IAT and Global Aerospace route 'api', but a
  // submission a human is LOGGING did not go out over the API — the API path
  // records its own row, with its own payload, through recordApiSubmission.
  // Seeding 'api' here would prefill an answer that is wrong every time it
  // fires, and it would show as blank in the control (no such option) while the
  // state said otherwise: a placeholder spelled like an answer.
};

/**
 * The method this carrier is usually reached by, or '' when nothing is known.
 * @param {object|null} carrier a carrier-options row
 * @returns {string} a MARKET_CHANNEL_OPTIONS value, or CHANNEL_UNSELECTED
 */
export function channelForCarrier(carrier) {
  if (!carrier) return CHANNEL_UNSELECTED;
  return ROUTING_TO_CHANNEL[carrier.routing_type] || CHANNEL_UNSELECTED;
}

/**
 * The wholesaler this carrier is usually reached through, or '' when none.
 * Only meaningful when the channel is cobroker.
 */
export function wholesalerForCarrier(carrier) {
  if (!carrier) return '';
  return channelForCarrier(carrier) === 'cobroker' ? (carrier.cobroker_slug || '') : '';
}

// "Through a wholesaler" is an answer that identifies nothing on its own, so the
// channel that means it REQUIRES saying which one. Shipped prompt 91; both doors
// read this rather than each testing the string.
export function needsWholesaler(channel) {
  return channel === 'cobroker';
}

/**
 * The wholesaler axis. carrier_slug stays the UNDERWRITER who writes the paper;
 * submitted_to_slug is who carried the submission to them.
 *
 * IT SORTS, IT DOES NOT GATE (prompt 94 item 1). is_cobroker is the right hint —
 * three carriers carry it and they are the three David actually routes through —
 * but a flag being right today is not a reason to make everything else
 * unreachable. The cobrokers lead; the rest follow under their own heading. The
 * only thing removed is the carrier already named as the underwriter, because a
 * market cannot carry a submission to itself.
 */
export function wholesalerOptions(carriers) {
  return (carriers || []).filter((c) => c && c.is_cobroker === true);
}

export function otherWholesalerOptions(carriers, excludeSlug = null) {
  return (carriers || []).filter((c) => c && c.is_cobroker !== true && c.slug !== excludeSlug);
}
